// src/frontend/src/pages/SimulationHistory.tsx - Fixed with proper Actions column
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { Conversation, ConversationStatus } from '../types/simulation';
import { conversationService } from '../api/simulationApi';
import api from '../api/api';

const SimulationHistory: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportingId, setExportingId] = useState<string | null>(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      setIsLoading(true);
      const response = await conversationService.getConversations();
      setConversations(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch coaching history');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get backend URL
  const getBackendUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:5050/api';
    }
    return process.env.REACT_APP_API_URL || '/api';
  };

  // Enhanced PDF export with multiple fallback methods
  const handleExportPDF = async (conversationId: string) => {
    try {
      setExportingId(conversationId);
      setError('');
      
      // Method 1: Try job-based approach
      try {
        const response = await api.post(`/conversations/${conversationId}/export/pdf`);
        const jobId = response.data.data?.jobId;
        
        if (jobId) {
          await pollForPDFCompletion(jobId);
          return;
        }
      } catch (jobError) {
        console.warn('Job-based export failed, trying direct approach:', jobError);
      }
      
      // Method 2: Direct PDF download
      try {
        const token = localStorage.getItem('token');
        const backendUrl = getBackendUrl();
        
        const response = await fetch(`${backendUrl}/conversations/${conversationId}/export/pdf-direct`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          downloadBlob(blob, `coaching-report-${conversationId}.pdf`);
          return;
        }
      } catch (directError) {
        console.warn('Direct export failed, trying fallback:', directError);
      }
      
      // Method 3: Form submission fallback
      await handleFormSubmissionExport(conversationId);
      
    } catch (error: any) {
      console.error('All export methods failed:', error);
      setError(`Failed to export PDF: ${error.message || 'Please try again later'}`);
    } finally {
      setExportingId(null);
    }
  };

  // Poll for PDF completion
  const pollForPDFCompletion = async (jobId: string) => {
    const maxAttempts = 30;
    let attempts = 0;
    
    return new Promise<void>((resolve, reject) => {
      const checkStatus = async () => {
        try {
          attempts++;
          const statusResponse = await api.get(`/conversations/export/status/${jobId}`);
          const status = statusResponse.data.data;
          
          if (status.status === 'completed') {
            const filename = status.result.pdfPath.split('/').pop();
            await downloadPDFFile(filename);
            resolve();
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'PDF generation failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('PDF generation timeout'));
          } else {
            setTimeout(checkStatus, 1000);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      checkStatus();
    });
  };

  // Download PDF file with authentication
  const downloadPDFFile = async (filename: string) => {
    const token = localStorage.getItem('token');
    const backendUrl = getBackendUrl();
    
    const response = await fetch(`${backendUrl}/conversations/export/download/${filename}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    
    const blob = await response.blob();
    downloadBlob(blob, filename);
  };

  // Utility to download blob
  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  };

  // Form submission fallback
  const handleFormSubmissionExport = async (conversationId: string) => {
    const token = localStorage.getItem('token');
    const backendUrl = getBackendUrl();
    
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${backendUrl}/conversations/${conversationId}/export/pdf`;
    form.target = '_blank';
    
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token';
    tokenInput.value = token || '';
    form.appendChild(tokenInput);
    
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get status color
  const getStatusColor = (status: ConversationStatus) => {
    switch (status) {
      case ConversationStatus.IN_PROGRESS:
        return 'bg-yellow-100 text-yellow-800';
      case ConversationStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case ConversationStatus.ABANDONED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status
  const formatStatus = (status: ConversationStatus) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Calculate stats
  const totalSessions = conversations.length;
  const completedSessions = conversations.filter(c => c.status === ConversationStatus.COMPLETED).length;
  const inProgressSessions = conversations.filter(c => c.status === ConversationStatus.IN_PROGRESS).length;
  
  const completedWithScores = conversations.filter(c => 
    c.status === ConversationStatus.COMPLETED && c.feedback?.overallScore
  );
  const averageScore = completedWithScores.length > 0 
    ? (completedWithScores.reduce((sum, c) => sum + (c.feedback?.overallScore || 0), 0) / completedWithScores.length).toFixed(1)
    : 'N/A';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Your Progress & History</h1>
            <p className="text-gray-600 mt-1">Review your past coaching sessions and track your improvement</p>
          </div>
          <button
            onClick={() => navigate('/coaching-sessions')}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            New Session
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={() => setError('')}
                className="text-red-600 hover:text-red-800 font-bold"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* Loading State */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your coaching history...</p>
          </div>
        ) : conversations.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No coaching sessions yet</h3>
            <p className="text-gray-500 mb-6">Start your first coaching session to build your history and track your progress</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => navigate('/learning-tracks')}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
              >
                Start Learning Tracks
              </button>
              <button
                onClick={() => navigate('/coaching-sessions')}
                className="border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
              >
                Browse Scenarios
              </button>
            </div>
          </div>
        ) : (
          /* Main Content */
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Table Container with horizontal scroll for mobile */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                {/* Table Header */}
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Scenario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                      Stakeholder
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                      Score
                    </th>
                    {/* FIXED: Ensure Actions column is always visible */}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                
                {/* Table Body */}
                <tbody className="bg-white divide-y divide-gray-200">
                  {conversations.map((conversation) => {
                    const scenario = typeof conversation.scenario === 'object' ? conversation.scenario : null;
                    const stakeholder = typeof conversation.stakeholderPersona === 'object' ? conversation.stakeholderPersona : null;
                    
                    return (
                      <tr key={conversation._id} className="hover:bg-gray-50 transition-colors">
                        {/* Scenario Column */}
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 mb-1">
                              {scenario?.title || 'Unknown Scenario'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {scenario?.category?.replace(/_/g, ' ') || 'uncategorized'}
                            </div>
                          </div>
                        </td>
                        
                        {/* Stakeholder Column */}
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="text-gray-900 mb-1">
                              {stakeholder?.name || 'Unknown Stakeholder'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {stakeholder?.type?.replace(/_/g, ' ') || 'unknown type'}
                            </div>
                          </div>
                        </td>
                        
                        {/* Status Column */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(conversation.status)}`}>
                            {formatStatus(conversation.status)}
                          </span>
                        </td>
                        
                        {/* Date Column */}
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(conversation.startedAt)}
                        </td>
                        
                        {/* Score Column */}
                        <td className="px-6 py-4">
                          {conversation.status === ConversationStatus.COMPLETED && conversation.feedback?.overallScore ? (
                            <span className={`inline-flex items-center px-2 py-1 text-sm font-medium rounded-full ${
                              conversation.feedback.overallScore >= 8 ? 'bg-green-100 text-green-800' :
                              conversation.feedback.overallScore >= 6 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {conversation.feedback.overallScore}/10
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>
                        
                        {/* FIXED: Actions Column - Always Visible */}
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {/* View Button */}
                            <button
                              onClick={() => navigate(`/coaching-sessions/${conversation._id}`)}
                              className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                            
                            {/* Export PDF Button - Only for completed sessions */}
                            {conversation.status === ConversationStatus.COMPLETED && (
                              <button
                                onClick={() => handleExportPDF(conversation._id)}
                                disabled={exportingId === conversation._id}
                                className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                  exportingId === conversation._id
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-green-100 text-green-600 hover:bg-green-200'
                                }`}
                              >
                                {exportingId === conversation._id ? (
                                  <>
                                    <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Exporting...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Export PDF
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Summary Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Total sessions: {totalSessions}</span>
                  <span className="mx-2">|</span>
                  <span className="font-medium">Completed: {completedSessions}</span>
                  <span className="mx-2">|</span>
                  <span className="font-medium">In Progress: {inProgressSessions}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Average Score: {averageScore}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SimulationHistory;